/**
 * Enum Input Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 */
import React from "react";

import { Box, MenuItem, TextField } from "@mui/material";

import { FieldSchema } from "../../types";
import {
  getEnumDisplayText,
  getEnumValuesForField,
  sortEnumValues,
} from "../../utils/enum-utils";

interface EnumInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const EnumInput: React.FC<EnumInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = parseInt(e.target.value, 10);
    onChange(isNaN(selectedValue) ? e.target.value : selectedValue);
  };

  // Get enum values with fallback logic
  const enumValues = getEnumValuesForField(field.name, field.enumValues);

  if (!enumValues || enumValues.length === 0) {
    return (
      <TextField
        name={field.name}
        label={field.name}
        placeholder="Enter value (no enum mapping)"
        value={String(value || "")}
        onChange={handleChange}
        error={!!error && touched}
        helperText={error && touched ? error : field.description}
        required={!field.isOptional}
        fullWidth
        size="small"
        variant="outlined"
        margin="none"
        sx={{
          "& .MuiFormHelperText-root": {
            margin: "4px 0 0 0",
          },
        }}
      />
    );
  }

  return (
    <TextField
      select
      name={field.name}
      label={field.name}
      value={value !== null && value !== undefined ? value : ""}
      onChange={handleChange}
      error={!!error && touched}
      helperText={error && touched ? error : field.description}
      required={!field.isOptional}
      fullWidth
      size="small"
      variant="outlined"
      margin="none"
      sx={{
        "& .MuiFormHelperText-root": {
          margin: "4px 0 0 0",
        },
      }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {sortEnumValues(enumValues).map((enumValue) => (
        <MenuItem key={enumValue.value} value={enumValue.value}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span style={{ fontWeight: 500 }}>
              {getEnumDisplayText(enumValue)}
            </span>
            <Box
              component="span"
              sx={{ color: "text.secondary", fontSize: "0.8rem", ml: 1 }}
            >
              {enumValue.value}
            </Box>
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );
};
